import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { PageLoader, TableSkeleton } from '../components/Loading';
import { useAuth } from '../context/AuthContext';
import { useDataRefresh } from '../context/DataRefreshContext';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Pencil, Gift, Users, MapPin, Trophy, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BonusTrackerPage() {
  const { isMD, user } = useAuth();
  const { refreshTrigger, triggerRefresh } = useDataRefresh();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editAgentOpen, setEditAgentOpen] = useState(false);
  const [editReferralOpen, setEditReferralOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [newCommissionRate, setNewCommissionRate] = useState('');
  const [incentiveNote, setIncentiveNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Get referral commission rate from settings (default 2%)
  const referralCommissionRate = settings?.referral_commission_rate || 2;

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/bonus-tracker`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching bonus tracker:', error);
      // For agents, this endpoint returns 403, which is expected
      if (!isMD) {
        // Agents get limited data from dashboard instead
        try {
          const dashRes = await axios.get(`${API}/dashboard`);
          setData({
            agent_commissions: [],
            customer_referrals: [],
            top_agents: dashRes.data.top_agents || []
          });
        } catch (e) {
          console.error('Error fetching dashboard:', e);
        }
      } else {
        toast.error('Failed to load bonus data');
      }
    } finally {
      setLoading(false);
    }
  };

  const openEditAgent = (agent) => {
    setSelectedAgent(agent);
    setNewCommissionRate(agent.commission_rate);
    setEditAgentOpen(true);
  };

  const openEditReferral = (referral) => {
    setSelectedReferral(referral);
    setIncentiveNote(referral.incentive_note || '');
    setEditReferralOpen(true);
  };

  const handleUpdateCommission = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/agents/${selectedAgent.agent_id}`, {
        commission_rate: parseFloat(newCommissionRate),
      });
      toast.success('Commission rate updated');
      setEditAgentOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating commission:', error);
      toast.error('Failed to update commission rate');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateIncentive = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/bonus-tracker/referral-incentive?referral_name=${encodeURIComponent(selectedReferral.referral_name)}&referral_phone=${encodeURIComponent(selectedReferral.referral_phone)}`,
        { incentive_note: incentiveNote }
      );
      toast.success('Incentive note updated');
      setEditReferralOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating incentive:', error);
      toast.error('Failed to update incentive note');
    } finally {
      setSaving(false);
    }
  };

  const totalCommissions = data?.agent_commissions?.reduce((sum, a) => sum + a.commission_earned, 0) || 0;
  const totalReferralValue = data?.customer_referrals?.reduce((sum, r) => sum + r.total_value, 0) || 0;

  // Get top agent by sales count
  const topAgentBySales = data?.agent_commissions?.length > 0 
    ? [...data.agent_commissions].sort((a, b) => b.deals - a.deals)[0] 
    : null;

  // For agents, find their own data
  const currentAgentData = !isMD && user?.email 
    ? data?.agent_commissions?.find(a => a.agent_email === user.email)
    : null;

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  // Agent View - Limited Access
  if (!isMD) {
    return (
      <Layout>
        <div className="space-y-6 fade-in" data-testid="bonus-tracker-page">
          {/* Header */}
          <div>
            <h1 className="font-heading text-4xl text-lux-text mb-2">Bonus Tracker</h1>
            <p className="text-lux-muted">View top performers and your progress</p>
          </div>

          {/* Top Agent Card */}
          <div className="lux-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Trophy className="w-6 h-6 text-lux-gold" />
              <h2 className="font-heading text-xl text-lux-gold">Top Agent This Period</h2>
            </div>
            {topAgentBySales ? (
              <div className="flex items-center justify-between p-4 bg-lux-row rounded-lg">
                <div>
                  <p className="text-lux-text font-semibold text-lg">{topAgentBySales.agent_name}</p>
                  <p className="text-lux-muted text-sm">{topAgentBySales.agent_code}</p>
                </div>
                <div className="text-right">
                  <p className="text-lux-gold font-bold text-2xl">{topAgentBySales.deals}</p>
                  <p className="text-lux-muted text-sm">Deals Closed</p>
                </div>
              </div>
            ) : (
              <p className="text-lux-muted text-center py-8">No sales data yet</p>
            )}
          </div>

          {/* Your Stats Card (if logged in as agent) */}
          {currentAgentData && (
            <div className="lux-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-blue-400" />
                <h2 className="font-heading text-xl text-blue-400">Your Performance</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-lux-row rounded-lg text-center">
                  <p className="text-lux-gold font-bold text-2xl">{currentAgentData.deals}</p>
                  <p className="text-lux-muted text-sm">Your Deals</p>
                </div>
                <div className="p-4 bg-lux-row rounded-lg text-center">
                  <p className="text-amber-400 font-bold text-2xl">{currentAgentData.land_sales || 0}</p>
                  <p className="text-lux-muted text-sm">Land Sales</p>
                </div>
              </div>
            </div>
          )}

          {/* Restricted Access Notice */}
          <div className="lux-card p-6 border-2 border-lux-border">
            <div className="flex items-center gap-3 text-lux-muted">
              <Lock className="w-5 h-5" />
              <p>Detailed commission data and customer referrals are restricted to MD access only.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // MD View - Full Access
  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="bonus-tracker-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-4xl text-lux-text mb-2">Bonus Tracker</h1>
          <p className="text-lux-muted">Track agent commissions and customer referral incentives</p>
        </div>

        <Tabs defaultValue="commissions" className="space-y-6">
          <TabsList className="bg-lux-card border border-lux-border p-1">
            <TabsTrigger
              value="commissions"
              className="data-[state=active]:bg-lux-gold data-[state=active]:text-lux-black"
              data-testid="tab-commissions"
            >
              <Users className="w-4 h-4 mr-2" />
              Agent Commissions
            </TabsTrigger>
            <TabsTrigger
              value="referrals"
              className="data-[state=active]:bg-lux-gold data-[state=active]:text-lux-black"
              data-testid="tab-referrals"
            >
              <Gift className="w-4 h-4 mr-2" />
              Customer Referrals
            </TabsTrigger>
          </TabsList>

          {/* Agent Commissions Tab */}
          <TabsContent value="commissions">
            <div className="lux-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="lux-table-header">
                    <tr>
                      <th className="px-6 py-4 text-left">Agent Name</th>
                      <th className="px-6 py-4 text-right">Deals</th>
                      <th className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <MapPin className="w-3 h-3 text-amber-400" />
                          Land Sales
                        </div>
                      </th>
                      <th className="px-6 py-4 text-right">Total Sale Value</th>
                      <th className="px-6 py-4 text-right">Commission %</th>
                      <th className="px-6 py-4 text-right">Commission Earned</th>
                      <th className="px-6 py-4 text-left">Shared With</th>
                      {isMD && <th className="px-6 py-4 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data?.agent_commissions?.map((agent) => (
                      <tr key={agent.agent_id} className="lux-table-row" data-testid={`commission-row-${agent.agent_id}`}>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-lux-text font-medium">{agent.agent_name}</p>
                            <p className="text-lux-muted text-xs font-mono">{agent.agent_code}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-lux-text">{agent.deals}</td>
                        <td className="px-6 py-4 text-right">
                          <span className="px-2 py-1 bg-amber-900/20 text-amber-400 rounded text-sm font-medium">
                            {agent.land_sales || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-lux-gold">
                          {formatCurrency(agent.total_sale_value)}
                        </td>
                        <td className="px-6 py-4 text-right text-lux-text">{agent.commission_rate}%</td>
                        <td className="px-6 py-4 text-right text-green-400 font-semibold">
                          {formatCurrency(agent.commission_earned)}
                        </td>
                        <td className="px-6 py-4 text-lux-muted text-sm">
                          {agent.shared_with?.length > 0 ? agent.shared_with.join(', ') : '-'}
                        </td>
                        {isMD && (
                          <td className="px-6 py-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditAgent(agent)}
                              className="text-lux-muted hover:text-lux-gold"
                              data-testid={`edit-commission-${agent.agent_id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-lux-row border-t-2 border-lux-gold">
                      <td className="px-6 py-4 font-heading text-lg text-lux-gold">Total</td>
                      <td className="px-6 py-4 text-right text-lux-text font-semibold">
                        {data?.agent_commissions?.reduce((sum, a) => sum + a.deals, 0) || 0}
                      </td>
                      <td className="px-6 py-4 text-right text-amber-400 font-semibold">
                        {data?.agent_commissions?.reduce((sum, a) => sum + (a.land_sales || 0), 0) || 0}
                      </td>
                      <td className="px-6 py-4 text-right text-lux-gold font-semibold">
                        {formatCurrency(data?.agent_commissions?.reduce((sum, a) => sum + a.total_sale_value, 0) || 0)}
                      </td>
                      <td className="px-6 py-4"></td>
                      <td className="px-6 py-4 text-right text-green-400 font-bold text-lg">
                        {formatCurrency(totalCommissions)}
                      </td>
                      <td className="px-6 py-4"></td>
                      {isMD && <td className="px-6 py-4"></td>}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Customer Referrals Tab */}
          <TabsContent value="referrals">
            <div className="lux-card overflow-hidden">
              {data?.customer_referrals?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="lux-table-header">
                      <tr>
                        <th className="px-6 py-4 text-left">Referral Name</th>
                        <th className="px-6 py-4 text-left">Phone</th>
                        <th className="px-6 py-4 text-right">Deals Referred</th>
                        <th className="px-6 py-4 text-right">Total Value</th>
                        <th className="px-6 py-4 text-right">Suggested Commission (2%)</th>
                        <th className="px-6 py-4 text-left">Incentive Note</th>
                        {isMD && <th className="px-6 py-4 text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {data.customer_referrals.map((referral, index) => (
                        <tr key={index} className="lux-table-row" data-testid={`referral-row-${index}`}>
                          <td className="px-6 py-4 text-lux-text font-medium">
                            {referral.referral_name}
                          </td>
                          <td className="px-6 py-4 text-lux-muted">{referral.referral_phone}</td>
                          <td className="px-6 py-4 text-right">
                            <span className="px-2 py-1 bg-blue-900/20 text-blue-400 rounded text-sm font-medium">
                              {referral.deals}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-lux-gold font-semibold">
                            {formatCurrency(referral.total_value)}
                          </td>
                          <td className="px-6 py-4 text-right text-green-400 font-semibold">
                            {formatCurrency(referral.suggested_commission || referral.total_value * (referralCommissionRate / 100))}
                          </td>
                          <td className="px-6 py-4 text-lux-muted text-sm max-w-xs truncate">
                            {referral.incentive_note || '-'}
                          </td>
                          {isMD && (
                            <td className="px-6 py-4 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditReferral(referral)}
                                className="text-lux-muted hover:text-lux-gold"
                                data-testid={`edit-referral-${index}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-lux-row border-t-2 border-lux-gold">
                        <td className="px-6 py-4 font-heading text-lg text-lux-gold">Total</td>
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4 text-right text-lux-text font-semibold">
                          {data.customer_referrals.reduce((sum, r) => sum + r.deals, 0)}
                        </td>
                        <td className="px-6 py-4 text-right text-lux-gold font-bold text-lg">
                          {formatCurrency(totalReferralValue)}
                        </td>
                        <td className="px-6 py-4 text-right text-green-400 font-bold">
                          {formatCurrency(data.customer_referrals.reduce((sum, r) => sum + (r.suggested_commission || r.total_value * (referralCommissionRate / 100)), 0))}
                        </td>
                        <td className="px-6 py-4"></td>
                        {isMD && <td className="px-6 py-4"></td>}
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <Gift className="w-16 h-16 text-lux-border mb-4" />
                  <p className="text-lux-muted text-lg">No customer referrals yet</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit Commission Modal */}
        <Dialog open={editAgentOpen} onOpenChange={setEditAgentOpen}>
          <DialogContent className="bg-lux-card border-lux-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-lux-text">
                Edit Commission Rate
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-lux-muted mb-4">
                Update commission rate for <span className="text-lux-gold">{selectedAgent?.agent_name}</span>
              </p>
              <div className="space-y-2">
                <label className="text-lux-text text-sm">Commission Rate (%)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={newCommissionRate}
                  onChange={(e) => setNewCommissionRate(e.target.value)}
                  className="lux-input"
                  data-testid="edit-commission-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditAgentOpen(false)} className="lux-btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleUpdateCommission} disabled={saving} className="lux-btn-primary" data-testid="save-commission-btn">
                {saving ? 'Saving...' : 'Update'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Referral Incentive Modal */}
        <Dialog open={editReferralOpen} onOpenChange={setEditReferralOpen}>
          <DialogContent className="bg-lux-card border-lux-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-lux-text">
                Edit Incentive Note
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-lux-muted mb-4">
                Add incentive note for <span className="text-lux-gold">{selectedReferral?.referral_name}</span>
              </p>
              <div className="space-y-2">
                <label className="text-lux-text text-sm">Incentive Note</label>
                <textarea
                  value={incentiveNote}
                  onChange={(e) => setIncentiveNote(e.target.value)}
                  className="lux-input w-full min-h-[100px] p-3"
                  placeholder="e.g., Gift voucher of ₦50,000"
                  data-testid="edit-incentive-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditReferralOpen(false)} className="lux-btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleUpdateIncentive} disabled={saving} className="lux-btn-primary" data-testid="save-incentive-btn">
                {saving ? 'Saving...' : 'Update'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
